/**
 * FotoLightbox - Modal de preview com navegação e edição
 *
 * Epic 2 - Story 2.3: Atualizado para exibir imagens reais da API
 * Quando arquivoUrl está disponível, exibe a imagem real do servidor
 * Suporte a teclado (← → Esc), CRUD
 */
import { useEffect, useCallback, useState, useRef } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  FileText,
  Image as ImageIcon,
  Trash2,
  Save,
  MapPin,
  Loader2,
  AlertCircle,
  ExternalLink,
  Download,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import type { MockFoto, ClassificacaoDocumento } from '../../hooks/useFotografias';
import { AuthenticatedImage } from '../AuthenticatedImage';
import apiClient from '../../services/apiClient';

interface FotoLightboxProps {
  isOpen: boolean;
  fotos: MockFoto[];
  currentIndex: number;
  classificacoes: ClassificacaoDocumento[];
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onUpdate: (id: number, updates: Partial<MockFoto>) => void;
  onDelete: (id: number) => void;
}

export function FotoLightbox({
  isOpen,
  fotos,
  currentIndex,
  classificacoes,
  onClose,
  onPrevious,
  onNext,
  onUpdate,
  onDelete,
}: FotoLightboxProps) {
  const [editedDescricao, setEditedDescricao] = useState('');
  const [editedClassificacao, setEditedClassificacao] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  // #16: zoom da foto (1x..4x). Clique alterna 1x<->2x; scroll ajusta fino.
  const [zoom, setZoom] = useState(1);
  // #16 (Rodada 3): pan (arrastar quando zoom>1) + tela cheia (expandir).
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const panningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const draggedRef = useRef(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const currentFoto = fotos[currentIndex];

  // Sincronizar estado local quando a foto muda
  useEffect(() => {
    if (currentFoto) {
      setEditedDescricao(currentFoto.descricao || '');
      setEditedClassificacao(currentFoto.classificacaoAbrev);
      setHasChanges(false);
      // Reset image loading state when photo changes
      setImageLoading(true);
      setImageError(false);
      setZoom(1); // #16: reseta o zoom ao trocar de foto
      setPan({ x: 0, y: 0 }); // #16: reseta o deslocamento (pan) ao trocar de foto
    }
  }, [currentFoto]);

  // #16: ao voltar para zoom 1x, recentraliza (zera o pan)
  useEffect(() => {
    if (zoom === 1) setPan({ x: 0, y: 0 });
  }, [zoom]);

  // Detect cached images: if already loaded in browser cache, skip spinner
  useEffect(() => {
    if (!currentFoto?.arquivoUrl || imageError) return;
    const raf = requestAnimationFrame(() => {
      const img = imgRef.current;
      if (img && img.complete && img.naturalWidth > 0) {
        setImageLoading(false);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [currentFoto, imageError]);

  // Timeout: if image hasn't loaded after 8s, show error state
  useEffect(() => {
    if (!currentFoto?.arquivoUrl || !imageLoading || imageError) return;
    const timer = setTimeout(() => {
      if (imageLoading) {
        setImageLoading(false);
        setImageError(true);
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [currentFoto, imageLoading, imageError]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          onPrevious();
          break;
        case 'ArrowRight':
          onNext();
          break;
      }
    },
    [isOpen, onClose, onPrevious, onNext]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !currentFoto) return null;

  const isPdf = currentFoto.type === 'pdf';

  const handleSave = () => {
    const classificacao = classificacoes.find(c => c.abreviacao === editedClassificacao);
    onUpdate(currentFoto.id, {
      descricao: editedDescricao,
      classificacaoAbrev: editedClassificacao,
      classificacao: classificacao?.classificacao || currentFoto.classificacao,
    });
    setHasChanges(false);
  };

  const handleDelete = () => {
    if (window.confirm(`Deseja excluir "${currentFoto.fileName}"?`)) {
      onDelete(currentFoto.id);
      onClose();
    }
  };

  const handleDescricaoChange = (value: string) => {
    setEditedDescricao(value);
    setHasChanges(true);
  };

  const handleClassificacaoChange = (value: string) => {
    setEditedClassificacao(value);
    setHasChanges(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Visualizador de foto"
    >
      {/* Modal Content */}
      <div
        className={`relative bg-white shadow-2xl overflow-hidden flex flex-col ${
          isFullscreen
            ? 'w-screen h-screen max-w-none max-h-none rounded-none'
            : 'w-full max-w-4xl max-h-[90vh] rounded-xl'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>

          {/* Navigation */}
          <div className="flex items-center gap-4">
            <button
              onClick={onPrevious}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
              disabled={fotos.length <= 1}
              aria-label="Foto anterior"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <span className="text-sm font-medium text-slate-600">
              {currentIndex + 1} / {fotos.length}
            </span>
            <button
              onClick={onNext}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
              disabled={fotos.length <= 1}
              aria-label="Próxima foto"
            >
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          {/* #16: botão tela cheia (expandir/restaurar) */}
          <button
            onClick={() => setIsFullscreen((f) => !f)}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label={isFullscreen ? 'Restaurar tamanho' : 'Tela cheia'}
            title={isFullscreen ? 'Restaurar' : 'Tela cheia'}
          >
            {isFullscreen ? (
              <Minimize2 className="w-5 h-5 text-slate-600" />
            ) : (
              <Maximize2 className="w-5 h-5 text-slate-600" />
            )}
          </button>
        </div>

        {/* Preview Area */}
        <div
          className="flex-1 flex items-center justify-center p-8 bg-slate-50 overflow-hidden relative"
          style={{ cursor: zoom > 1 ? 'grab' : 'default' }}
          onWheel={(e) => {
            // #16: scroll ajusta o zoom (1x..4x)
            if (isPdf || !currentFoto.arquivoUrl) return;
            setZoom((z) => {
              const next = z + (e.deltaY < 0 ? 0.25 : -0.25);
              return Math.min(4, Math.max(1, Math.round(next * 100) / 100));
            });
          }}
          onMouseDown={(e) => {
            // #16: inicia o arraste (pan) quando ampliado
            if (zoom <= 1 || isPdf || !currentFoto.arquivoUrl) return;
            panningRef.current = true;
            draggedRef.current = false;
            panStartRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
          }}
          onMouseMove={(e) => {
            if (!panningRef.current) return;
            const nx = e.clientX - panStartRef.current.x;
            const ny = e.clientY - panStartRef.current.y;
            if (Math.abs(nx - pan.x) > 2 || Math.abs(ny - pan.y) > 2) draggedRef.current = true;
            setPan({ x: nx, y: ny });
          }}
          onMouseUp={() => { panningRef.current = false; }}
          onMouseLeave={() => { panningRef.current = false; }}>

          {isPdf ? (
            // PDF Preview
            <div className="flex flex-col items-center">
              <FileText className="w-24 h-24 text-red-400 mb-4" />
              <span className="text-lg font-medium text-slate-700">
                {currentFoto.fileName}
              </span>
              <span className="text-sm text-slate-500 mt-1">Documento PDF</span>
              {currentFoto.arquivoUrl && (
                <a
                  href={currentFoto.arquivoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Abrir PDF
                </a>
              )}
            </div>
          ) : currentFoto.arquivoUrl ? (
            // Real Image from API
            <div className="flex flex-col items-center w-full h-full">
              {/* Loading indicator */}
              {imageLoading && !imageError && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-50">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
              )}
              {/* Error state */}
              {imageError && (
                <div className="flex flex-col items-center">
                  <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
                  <span className="text-lg font-medium text-slate-700">
                    Erro ao carregar imagem
                  </span>
                  <span className="text-sm text-slate-500 mt-1">
                    {currentFoto.fileName}
                  </span>
                  <a
                    href={currentFoto.arquivoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Abrir em nova aba
                  </a>
                </div>
              )}
              {/* Real image */}
              <AuthenticatedImage
                src={currentFoto.arquivoUrl}
                alt={currentFoto.fileName}
                className={`max-w-full max-h-full object-contain rounded-lg shadow-lg transition-transform duration-150 ${
                  imageLoading || imageError ? 'hidden' : ''
                }`}
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  cursor: zoom > 1 ? 'grab' : 'zoom-in',
                }}
                onClick={(e: any) => {
                  // #16: clique alterna 1x <-> 2x — mas se foi arraste (pan), nao alterna
                  e.stopPropagation();
                  if (draggedRef.current) { draggedRef.current = false; return; }
                  setZoom((z) => (z > 1 ? 1 : 2));
                }}
                onLoad={() => setImageLoading(false)}
                onError={() => {
                  setImageLoading(false);
                  setImageError(true);
                }}
              />
              {/* GPS indicator below image */}
              {!imageLoading && !imageError && currentFoto.hasGps && (
                <div className="mt-3 flex items-center gap-2 text-sm text-green-600">
                  <MapPin className="w-4 h-4" />
                  <span>{currentFoto.gpsDisplay || 'Localização disponível'}</span>
                </div>
              )}
            </div>
          ) : (
            // Fallback: No URL available (mockup mode)
            <div className="flex flex-col items-center">
              <ImageIcon className="w-24 h-24 text-slate-400 mb-4" />
              <span className="text-lg font-medium text-slate-700">
                {currentFoto.fileName}
              </span>
              <span className="text-sm text-slate-500 mt-1">
                {currentFoto.tamanho}
              </span>
            </div>
          )}
        </div>

        {/* Details & Edit Form */}
        <div className="p-4 border-t border-slate-200 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* File Info */}
            <div>
              <div className="text-xs text-slate-500 mb-1">Arquivo</div>
              <div className="text-sm font-medium text-slate-800">
                {currentFoto.fileName}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {isPdf ? 'PDF' : 'JPEG'} • {currentFoto.tamanho} •{' '}
                {currentFoto.cliente}
              </div>
              {/* GPS Info - Epic 2 Story 2.4 */}
              {currentFoto.hasGps && (
                <div className="flex items-center gap-1.5 mt-2 text-xs text-green-600">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>{currentFoto.gpsDisplay || 'Com GPS'}</span>
                </div>
              )}
            </div>

            {/* Classification Select */}
            <div>
              <label className="text-xs text-slate-500 mb-1 block">
                Categoria
              </label>
              <select
                value={editedClassificacao}
                onChange={(e) => handleClassificacaoChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {classificacoes.map((c) => (
                  <option key={c.id} value={c.abreviacao}>
                    {c.classificacao}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Description */}
          <div className="mt-4">
            <label className="text-xs text-slate-500 mb-1 block">
              Observacao
            </label>
            <textarea
              value={editedDescricao}
              onChange={(e) => handleDescricaoChange(e.target.value)}
              placeholder="Adicione uma observacao..."
              rows={2}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Excluir
              </button>

              {/* Story 23.3: Download button (blob fetch for cross-origin filename) */}
              {currentFoto.arquivoUrl && (
                <button
                  onClick={async () => {
                    try {
                      // Fetch via apiClient (Bearer header) → blob URL → download
                      // NUNCA passa JWT na URL — sem ?token= leaking
                      const url = currentFoto.arquivoUrl!;
                      const urlForApi = url.startsWith('/api/') ? url.slice(4) : url;
                      const response = await apiClient.get(urlForApi, { responseType: 'blob' });
                      const blob = response.data as Blob;
                      const objectUrl = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = objectUrl;
                      a.download = currentFoto.fileName;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(objectUrl);
                    } catch (err) {
                      console.error('[FotoLightbox] Download failed:', err);
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              )}
            </div>

            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                hasChanges
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              <Save className="w-4 h-4" />
              Salvar Alteracoes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FotoLightbox;
